import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import { getServiceRequestSummary, getSrActivityLogs, getWoActivityLogs } from './api';
import { type ActivityItem, type ActivityLogState, initialState } from './types';


/**
 * Normalizes array of activities into source and logIds structure
 */
function normalizeActivities(activities: ActivityItem[] = []): {
  source: Record<string, ActivityItem>;
  logIds: string[];
} {
  const source: Record<string, ActivityItem> = {};
  const logIds: string[] = [];

  activities?.forEach((activity) => {
    if (activity?.activity_log_id) {
      source[activity.activity_log_id] = activity;
      logIds.push(activity.activity_log_id);
    }
  });

  return { source, logIds };
}

const extraReducers = (builder: ActionReducerMapBuilder<ActivityLogState>) => {
  // ----- WORK ORDER ACTIVITY LOGS -----
  builder.addCase(getWoActivityLogs().pending, (state) => {
    state.loading = true;
  });

  builder.addCase(getWoActivityLogs().fulfilled, (state, action) => {
    const { data, meta } = action.payload;
    const workOrderId = action?.meta?.arg?.work_order_id;
    const prev = meta?.prev;

    // Set context if not set
    if (!state.currentSrOrWoId && workOrderId) {
      state.currentSrOrWoId = workOrderId;
      state.currentPage = 'work-orders';
    }

    if (prev) {
      // For "prev" pagination, append older activities at the bottom
      const { source: newSource, logIds: newIds } = normalizeActivities(data);

      const mergedSource = { ...state.source, ...newSource };

      // Avoid duplicates
      const existingIdsSet = new Set(state.logIds);
      const uniqueNewIds = newIds.filter((id) => !existingIdsSet.has(id));

      // append older logs to bottom
      state.logIds = [...state.logIds, ...uniqueNewIds];

      state.source = mergedSource;
    } else {
      // Clear drafted notes and expanding groups on new fetch
      state.draftedInternalNote = initialState.draftedInternalNote;
      state.expandingGroups = initialState.expandingGroups;

      // First page - replace all activities
      const normalized = normalizeActivities(data);
      state.source = normalized.source;
      state.logIds = normalized.logIds;
    }

    state.meta = meta;
    state.loading = false;
  });

  builder.addCase(getWoActivityLogs().rejected, (state) => {
    state.loading = false;
  });

  // ----- SERVICE REQUEST ACTIVITY LOGS -----
  builder.addCase(getSrActivityLogs().pending, (state) => {
    state.loading = true;
  });

  builder.addCase(getSrActivityLogs().fulfilled, (state, action) => {
    const { data, meta } = action.payload;
    const serviceRequestId = action?.meta?.arg?.service_request_id;
    const prev = meta?.prev;

    // Set context if not set
    if (!state.currentSrOrWoId && serviceRequestId) {
      state.currentSrOrWoId = serviceRequestId;
      state.currentPage = 'service-requests';
    }

    if (prev) {
      // For "prev" pagination, append older activities at the bottom
      const { source: newSource, logIds: newIds } = normalizeActivities(data);

      const mergedSource = { ...state.source, ...newSource };

      // Avoid duplicates
      const existingIdsSet = new Set(state.logIds);
      const uniqueNewIds = newIds.filter((id) => !existingIdsSet.has(id));

      // append older logs to bottom
      state.logIds = [...state.logIds, ...uniqueNewIds];

      state.source = mergedSource;
    } else {
      // Clear drafted notes and expanding groups on new fetch
      state.draftedInternalNote = initialState.draftedInternalNote;
      state.expandingGroups = initialState.expandingGroups;

      // First page - replace all activities
      const normalized = normalizeActivities(data);
      state.source = normalized.source;
      state.logIds = normalized.logIds;
    }

    state.meta = meta;
    state.loading = false;
  });

  builder.addCase(getSrActivityLogs().rejected, (state) => {
    state.loading = false;
  });

  // ----- SERVICE REQUEST SUMMARY -----
  builder.addCase(getServiceRequestSummary().pending, (state) => {
    state.isFetchingSummary = true;
    state.hasSummaryApiFailed = false;
  });

  builder.addCase(getServiceRequestSummary().fulfilled, (state, action) => {
    state.summary = action.payload ?? {};
    state.isFetchingSummary = false;
    state.hasSummaryApiFailed = false;
  });

  builder.addCase(getServiceRequestSummary().rejected, (state, action) => {
    state.isFetchingSummary = false;
    if (!isCanceledError(action.payload)) {
      state.hasSummaryApiFailed = true;
    }
  });
};
export default extraReducers;
